import OpenAI from 'openai';
import { ANALYSIS_SYSTEM_PROMPT } from './prompts';
import { ApiError } from '../errors';
import { 
  OPENAI_MODELS,
  getOptimalModel,
  MODEL_CONFIGS,
  type TaskType
} from '../../config/openai/models';
import { optimizedCompletion } from './speedOptimizer';

const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY;

if (!OPENAI_API_KEY) {
  throw new Error('Missing OpenAI API key');
}

export const openai = new OpenAI({
  apiKey: OPENAI_API_KEY,
  dangerouslyAllowBrowser: true // Only for demo purposes
});

export async function analyzeWithGPT4(
  cvText: string,
  jobDescription: string,
  taskType: TaskType = 'candidate-analysis',
  model?: keyof typeof MODEL_CONFIGS
) {
  // Use intelligent model selection if no specific model provided
  const selectedModel = model || getOptimalModel(taskType);
  // Input validation
  if (!cvText?.trim()) {
    throw new ApiError('CV text is required', 400);
  }

  if (!jobDescription?.trim()) {
    throw new ApiError('Job description is required', 400);
  }

  // Model validation
  if (!Object.values(OPENAI_MODELS).includes(selectedModel)) {
    throw new ApiError(
      `Invalid model. Must be one of: ${Object.values(OPENAI_MODELS).join(', ')}`,
      400
    );
  }

  try {
    const modelConfig = MODEL_CONFIGS[selectedModel];

    // Validate token limits
    const totalInputLength = cvText.length + jobDescription.length;
    if (totalInputLength > modelConfig.maxTokens * 4) { // Rough char to token ratio
      throw new ApiError('Input text is too long for the selected model', 400);
    }

    const response = await openai.chat.completions.create({
      model: selectedModel,
      messages: [
        {
          role: 'system',
          content: ANALYSIS_SYSTEM_PROMPT
        },
        {
          role: 'user',
          content: `CV:\n${cvText}\n\nJob Description:\n${jobDescription}`
        }
      ],
      temperature: modelConfig.temperature,
      presence_penalty: modelConfig.presencePenalty,
      frequency_penalty: modelConfig.frequencyPenalty,
      max_tokens: modelConfig.maxTokens
    });

    if (!response?.choices?.length) {
      throw new ApiError('Invalid response from OpenAI', 500);
    }

    const content = response.choices[0]?.message?.content;
    if (!content) {
      throw new ApiError('Empty response from OpenAI', 500);
    }

    return content;

  } catch (error) {
    if (error instanceof ApiError) {
      throw error; // Re-throw our custom API errors
    }

    if (error instanceof OpenAI.APIError) {
      // Handle specific OpenAI error types
      switch (error.status) {
        case 400:
          throw new ApiError('Invalid request to OpenAI API: ' + error.message, 400);
        case 401:
          throw new ApiError('Invalid OpenAI API key', 401);
        case 429:
          throw new ApiError('OpenAI API rate limit exceeded. Please try again later.', 429);
        case 500:
          throw new ApiError('OpenAI service error. Please try again later.', 500);
        default:
          throw new ApiError('OpenAI API error: ' + error.message, error.status || 500);
      }
    }

    // Handle network or other errors
    if (error instanceof Error) {
      throw new ApiError(
        'Failed to communicate with OpenAI: ' + error.message,
        500
      );
    }

    throw new ApiError('An unexpected error occurred during analysis', 500);
  }
}

export async function transformQuestionsForPerspective(
  questions: string[],
  perspective: string,
  candidateName: string,
  jobTitle: string,
  cvSummary: string
) {
  if (!questions?.length) {
    throw new ApiError('Questions array is required', 400);
  }

  try {
    const perspectiveDescriptions = {
      'hr': {
        role: 'HR Manager',
        focus: 'Cultural fit, policy compliance, team dynamics, communication skills, work-life balance, and career development',
        context: 'You focus on how the candidate will integrate with company culture, handle interpersonal relationships, and align with organizational values.'
      },
      'manager': {
        role: 'Direct Manager',
        focus: 'Day-to-day performance, task execution, project management, team collaboration, and direct reporting relationships',
        context: 'You focus on hands-on work capabilities, accountability, communication with immediate team, and practical job execution.'
      },
      'senior-manager': {
        role: 'Senior Manager',
        focus: 'Strategic thinking, leadership potential, cross-functional impact, process improvement, and mentoring abilities',
        context: 'You focus on broader organizational impact, leadership capacity, strategic alignment, and potential for growth and influence.'
      },
      'c-level': {
        role: 'C-Level Executive',
        focus: 'Vision alignment, business impact, organizational growth, market understanding, and long-term strategic value',
        context: 'You focus on business outcomes, strategic vision, competitive advantage, and how this hire contributes to company objectives.'
      },
      'technical-lead': {
        role: 'Technical Lead',
        focus: 'Technical expertise, architecture decisions, code quality, technology trends, and technical mentoring',
        context: 'You focus on technical depth, implementation approaches, technology choices, and technical leadership capabilities.'
      }
    };

    const perspectiveInfo = perspectiveDescriptions[perspective as keyof typeof perspectiveDescriptions];
    if (!perspectiveInfo) {
      throw new ApiError('Invalid perspective provided', 400);
    }

    const systemPrompt = `You are an expert interview question transformer. Your job is to take generic interview questions and intelligently adapt them for specific interviewer perspectives while keeping them candidate-specific and maintaining their assessment value.

TRANSFORMATION PRINCIPLES:
1. Keep the core competency being tested the same
2. Adjust the framing, context, and follow-up focus to match the interviewer's priorities  
3. Use the interviewer's language and concerns
4. Maintain specificity to the candidate's background
5. Ensure questions remain practical and actionable
6. Don't change technical terms or candidate-specific details unnecessarily

PERSPECTIVE: ${perspectiveInfo.role}
FOCUS AREAS: ${perspectiveInfo.focus}
CONTEXT: ${perspectiveInfo.context}

CANDIDATE: ${candidateName}
JOB TITLE: ${jobTitle}
CANDIDATE BACKGROUND: ${cvSummary}

Transform each question to reflect how a ${perspectiveInfo.role} would ask it, considering their specific priorities and concerns. Keep the questions authentic and natural, not templated.

Return the transformed questions as a JSON array of strings, maintaining the same order as the input.`;

    const userPrompt = `Transform these interview questions for a ${perspectiveInfo.role} perspective:

${questions.map((q, i) => `${i + 1}. ${q}`).join('\n\n')}

Return as JSON array: ["transformed question 1", "transformed question 2", ...]`;

    const response = await openai.chat.completions.create({
      model: OPENAI_MODELS.GPT_3_5_TURBO,
      messages: [
        {
          role: 'system',
          content: systemPrompt
        },
        {
          role: 'user',
          content: userPrompt
        }
      ],
      temperature: 0.7,
      max_tokens: 2000
    });

    if (!response?.choices?.length) {
      throw new ApiError('Invalid response from OpenAI', 500);
    }

    const content = response.choices[0]?.message?.content;
    if (!content) {
      throw new ApiError('Empty response from OpenAI', 500);
    }

    // Parse the JSON response
    try {
      const transformedQuestions = JSON.parse(content);
      if (!Array.isArray(transformedQuestions)) {
        throw new Error('Response is not an array');
      }
      return transformedQuestions;
    } catch (parseError) {
      console.error('Failed to parse transformed questions:', content);
      // Fallback: return original questions if parsing fails
      return questions;
    }

  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    if (error instanceof OpenAI.APIError) {
      console.error('OpenAI API error in question transformation:', error);
      // Fallback: return original questions
      return questions;
    }

    console.error('Error transforming questions:', error);
    // Fallback: return original questions
    return questions;
  }
}

export async function analyzeBiasWithGPT(
  jobDescription: string,
  jobTitle?: string,
  companyName?: string,
  analysisType: 'quick' | 'standard' | 'comprehensive' = 'standard'
) {
  // Input validation
  if (!jobDescription?.trim()) {
    throw new ApiError('Job description is required', 400);
  }

  if (jobDescription.trim().length < 50) {
    throw new ApiError('Job description is too short for meaningful analysis', 400);
  }

  try {
    // Use single comprehensive analysis instead of multiple passes to avoid token issues
    const result = await performStreamlinedBiasAnalysis(
      jobDescription, 
      jobTitle, 
      companyName, 
      analysisType
    );

    return result;

  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    if (error instanceof OpenAI.APIError) {
      switch (error.status) {
        case 400:
          throw new ApiError('Invalid request to OpenAI API: ' + error.message, 400);
        case 401:
          throw new ApiError('Invalid OpenAI API key', 401);
        case 429:
          throw new ApiError('OpenAI API rate limit exceeded. Please try again later.', 429);
        case 500:
          throw new ApiError('OpenAI service error. Please try again later.', 500);
        default:
          throw new ApiError('OpenAI API error: ' + error.message, error.status || 500);
      }
    }

    if (error instanceof Error) {
      throw new ApiError(
        'Failed to communicate with OpenAI: ' + error.message,
        500
      );
    }

    throw new ApiError('An unexpected error occurred during bias analysis', 500);
  }
}

// Streamlined bias analysis using optimized completion
async function performStreamlinedBiasAnalysis(
  jobDescription: string,
  jobTitle?: string,
  companyName?: string,
  _analysisType: string = 'standard' // Prefixed with underscore to indicate intentionally unused
) {
  const systemPrompt = `You are a world-class expert in unconscious bias detection and inclusive hiring practices with deep knowledge of implicit bias patterns, legal compliance, and cultural competency.

BIAS DETECTION EXPERTISE:
🔍 GENDER BIAS: Masculine/feminine-coded words, gendered assumptions, role stereotyping
⏰ AGE BIAS: "Digital native", experience requirements, technology assumptions
🌍 RACIAL/ETHNIC BIAS: Cultural name preferences, language requirements, "cultural fit"
🏛️ CULTURAL BIAS: Western-centric holidays, Christian-normative assumptions
♿ DISABILITY BIAS: Unnecessary physical requirements, lack of accommodation language
💰 SOCIOECONOMIC BIAS: Unpaid requirements, prestigious university preferences

Analyze for subtle, implicit, and systemic biases that may unconsciously exclude qualified diverse candidates.

RESPOND WITH ONLY VALID JSON - NO MARKDOWN FORMATTING.`;

  const userPrompt = `Perform comprehensive bias analysis on this job description:

POSITION: ${jobTitle || 'Not specified'}
COMPANY: ${companyName || 'Not specified'}

JOB DESCRIPTION:
"""
${jobDescription}
"""

Return complete analysis as JSON with ALL required fields:
{
  "overall_score": <number 0-100>,
  "bias_count": <number>,
  "detected_biases": [
    {
      "type": "gender|age|racial|cultural|disability|socioeconomic",
      "severity": "low|medium|high", 
      "description": "bias description",
      "examples": ["quote1", "quote2"],
      "suggestion": "specific improvement"
    }
  ],
  "recommendations": ["recommendation1", "recommendation2"],
  "improvement_areas": ["area1", "area2"],
  "gender_bias_score": <number 0-100>,
  "age_bias_score": <number 0-100>,
  "racial_bias_score": <number 0-100>,
  "cultural_bias_score": <number 0-100>,
  "disability_bias_score": <number 0-100>,
  "socioeconomic_bias_score": <number 0-100>,
  "confidence_score": <number 0-1>,
  "analysis_summary": "brief summary",
  "priority_actions": ["action1", "action2"]
}`;

  const response = await optimizedCompletion(
    'bias-detection',
    [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ],
    { 
      useCache: true,
      // Increase token limit for comprehensive analysis
      max_tokens: 4000
    }
  );

  const content = response.choices[0]?.message?.content;
  if (!content) {
    throw new ApiError('Empty response from bias analysis', 500);
  }
  
  // Clean the response to ensure it's valid JSON (remove markdown formatting)
  const cleanedContent = content.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
  
  try {
    const parsed = JSON.parse(cleanedContent);
    
    // Validate required fields and provide defaults if missing
    return {
      overall_score: parsed.overall_score || 75,
      bias_count: parsed.bias_count || 0,
      detected_biases: parsed.detected_biases || [],
      recommendations: parsed.recommendations || ['Complete analysis unavailable. Please try again.'],
      improvement_areas: parsed.improvement_areas || ['Analysis incomplete'],
      gender_bias_score: parsed.gender_bias_score || 75,
      age_bias_score: parsed.age_bias_score || 75,
      racial_bias_score: parsed.racial_bias_score || 75,
      cultural_bias_score: parsed.cultural_bias_score || 75,
      disability_bias_score: parsed.disability_bias_score || 75,
      socioeconomic_bias_score: parsed.socioeconomic_bias_score || 75,
      confidence_score: parsed.confidence_score || 0.8,
      analysis_summary: parsed.analysis_summary || 'Bias analysis completed.',
      priority_actions: parsed.priority_actions || ['Review job description for inclusive language']
    };
  } catch (parseError) {
    console.error('Failed to parse bias analysis JSON:', cleanedContent);
    console.error('Parse error:', parseError);
    
    // Return fallback analysis instead of throwing error
    return {
      overall_score: 70,
      bias_count: 0,
      detected_biases: [],
      recommendations: ['Unable to complete full analysis due to response format. Please try again with a more detailed job description.'],
      improvement_areas: ['Analysis format error'],
      gender_bias_score: 70,
      age_bias_score: 70,
      racial_bias_score: 70,
      cultural_bias_score: 70,
      disability_bias_score: 70,
      socioeconomic_bias_score: 70,
      confidence_score: 0.3,
      analysis_summary: 'Analysis was incomplete due to response format issues.',
      priority_actions: ['Re-run analysis', 'Review job description manually for bias']
    };
  }
}